import { CLUSTER_TYPE_NAME, CM_SAMPLE_DATA_ROWS_COUNT } from '../../../constants'
import { parameterizeArrayofObjects } from '../../../utils/graphqlUtil'
import {
  CommunicationModeNode,
  ConsentManagementTableSample,
  ConsentManagementTableSamplesParams,
  DatabasesAndClustersOption,
  DatasourceDetail,
  DatasourceListNode,
  DatasourceToAdd,
  StructTable,
  StructTablesParams,
  SubscriptionNode
} from '../consentManagementSlice'
import { ConsentOptStatus } from '../constants'
import { gql } from 'graphql-request'

type subscriptionresult = {
  list: SubscriptionNode[]
  count: number
}

export const queryConsentManagementSettingsDatasourceList = (datasourceTypes: string[]): string => {
  return datasourceTypes.length > 0
    ? gql`
        {
          consentManagementSettings {
            edges {
              node {
                datasource {
                  edges {
                    node {
                      communicationPreferences {
                        count
                      }
                      type
                      name
                      id
                    }
                  }
                }
                alertCount
              }
            }
          }
          consentStats: consentStats(filter: [{ key: SOURCE, values: "DATASOURCE_PREFERENCE" }]) {
            edges {
              node {
                source
                datasource {
                  edges {
                    node {
                      id
                    }
                  }
                }
                consentStats {
                  total
                }
              }
            }
          }
        }
      `
    : ''
}

export const mapConsentManagementSettingsDatasourceList = (
  raw: any
): { datasourceList: DatasourceListNode[] } => {
  const mappedData: DatasourceListNode[] = []
  const dsConsentsMap: any = {}

  raw.consentStats.edges.forEach(({ node }) => {
    const datasourceId = node.datasource.edges[0]?.node.id

    if (node.source === 'DATASOURCE_PREFERENCE') {
      dsConsentsMap[datasourceId] = dsConsentsMap[datasourceId]
        ? dsConsentsMap[datasourceId] + node.consentStats.total
        : node.consentStats.total
    }
  })

  raw?.consentManagementSettings?.edges?.forEach((edge) => {
    edge?.node?.datasource?.edges?.forEach((ds) => {
      mappedData.push({
        icon: ds?.node?.type,
        title: ds?.node?.name,
        subscriptionCount: ds?.node?.communicationPreferences?.count,
        consents: dsConsentsMap[ds?.node?.id] || 0,
        alerts: edge?.node?.alertCount,
        id: ds?.node?.id
      })
    })
  })

  return { datasourceList: mappedData || [] }
}

export const queryConsentManagementSettingsDatasourcesToAdd = (
  datasourceTypes: string[]
): string => {
  return gql`
    {
      datasources(filter: [{key:TYPE,values:${JSON.stringify(datasourceTypes)}}]) {
        edges {
          node {
            id
            name
            type
          }
        }
      }
    }
  `
}

export const mapConsentManagementSettingsDatasourcesToAdd = (raw: any): DatasourceToAdd[] => {
  return raw?.datasources?.edges?.map((edge) => ({
    id: edge?.node?.id,
    name: edge?.node?.name,
    type: edge?.node?.type
  }))
}

export const querySubscription = (id: string) => {
  return gql`
    {
      communicationPreferences (id: "${id}") {
        edges {
          node {
            name
            description
            type
            configuration {
              ... on HubspotCommunicationPreference {
                __typename
              }
              ... on CustomCommunicationPreference {
                __typename
                communicationMethods {
                  communicationMethod
                  displayCommunicationMethod
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapSubscription = (raw: any) => {
  const subscription = raw?.communicationPreferences?.edges?.[0]?.node
  const communicationMethods = subscription?.configuration?.communicationMethods || []
  return {
    name: subscription?.name,
    description: subscription?.description,
    type: subscription?.type,
    communicationMethods
  }
}

export const querySubscriptionList = (params): string => {
  const dataSourceId = params?.filters?.[0]?.values?.[0]
  const filtersParameterized = parameterizeArrayofObjects(params.filters || [])
  const filterParams = filtersParameterized ? `(filter: ${filtersParameterized})` : ''
  return gql`
    {
      communicationPreferences ${filterParams} {
        count
        edges {
          node {
            id
            name
            description
            type
            configuration{
              ... on HubspotCommunicationPreference
              {
                __typename
              }
              ... on CustomCommunicationPreference
              {
                __typename
                communicationMethods {
                  communicationMethod
                  displayCommunicationMethod
                }
              }
            }
            preferenceCenter {
              count
              edges {
                node {
                  name
                }
              }
            }
          }
        }
      }

      consentStats(filter: [{key: SOURCE, values: "DATASOURCE_PREFERENCE"} ${
        dataSourceId ? `, {key: DATASOURCE_ID, values: "${dataSourceId}"}` : ''
      }]) {
        edges {
          node {
            preference {
              edges {
                node {
                  id
                }
              }
            }
            consentStats {
              optIn
              optOut
            }
          }
        }
      }
    }
  `
}

export const mapQuerySubscriptionList = (raw: any): subscriptionresult => {
  const count = raw?.communicationPreferences?.count || 0
  const mappedData: SubscriptionNode[] = []
  const preferenceToConsentsMap = {}

  raw?.consentStats?.edges?.forEach(({ node }) => {
    const preference = node?.preference?.edges[0]?.node
    if (preference) {
      preferenceToConsentsMap[preference.id] = {
        optIn: node.consentStats.optIn,
        optOut: node.consentStats.optOut
      }
    }
  })

  raw?.communicationPreferences?.edges?.forEach((preferenceNode) => {
    const preference = preferenceNode?.node

    const communicationModeCount: number =
      preference?.type === 'HUBSPOT'
        ? 1
        : Array.isArray(preference?.configuration?.communicationMethods)
        ? preference?.configuration?.communicationMethods?.length
        : 1

    const preferenceCenterNames = preference?.preferenceCenter?.edges?.map(
      (center) => center?.node?.name
    )

    mappedData.push({
      id: preference?.id,
      subscriptionName: preference?.name,
      description: preference?.description,
      optInCount: preferenceToConsentsMap[preference?.id]?.optIn || 0,
      optOutCount: preferenceToConsentsMap[preference?.id]?.optOut || 0,
      communicationModeCount,
      preferenceCenterCount: preference?.preferenceCenter?.count,
      preferenceCenterNames
    })
  })

  return { list: mappedData || [], count }
}

export const mapQueryConsentLogSubscriptionFilters = (raw: any) => {
  return raw?.communicationPreferences?.edges?.map(({ node }) => ({ id: node.id, name: node.name }))
}

export const queryConsentManagementSettingsSubscriptionListCommunicationMode = (
  datasourceId: string,
  preferenceId: string
): string => {
  return gql`
    {
      consentHistoryGroupByCommunicationType(
        filter: [
          { key: DATASOURCE_IDS, values: "${datasourceId}" },
          { key: PREFERENCE, values: ["${preferenceId}"] }
        ]
      ) {
        edges {
          node {
            communicationType
            optin: consentHistory(
              filter: [
                { key: DATASOURCE_IDS, values: "${datasourceId}" },
                { key: PREFERENCE, values: ["${preferenceId}"] },
                { key: OPT_STATUS, values: ["${ConsentOptStatus.Subscribed}"] }
              ]
            ) {
              count
            }
            optout: consentHistory(
              filter: [
               { key: DATASOURCE_IDS, values: "${datasourceId}" },
              { key: PREFERENCE, values: ["${preferenceId}"] },
                { key: OPT_STATUS, values: ["${ConsentOptStatus.UnSubscribed}"] }
              ]
            ) {
              count
            }
          }
        }
      }
    }
  `
}

export const mapConsentManagementSettingsSubscriptionListCommunicationMode = (
  raw: any
): CommunicationModeNode[] => {
  return (
    raw.consentHistoryGroupByCommunicationType?.edges?.map((edge) => {
      const { communicationType, optin, optout } = edge?.node

      return {
        modeName: communicationType,
        optIn: optin?.count,
        optOut: optout?.count
      }
    }) || []
  )
}

export const queryConsentManagementSettingsDatasourceDetail = (
  datasourceDetailId: string
): string => {
  return datasourceDetailId
    ? gql`{
   datasources(id:"${datasourceDetailId}"){
    edges{
      node{
        type
        name
        status: state
        consentStats{
            edges{
              node{
                consentStats{
                  total
                }
              }
            }
        }
        communicationPreferences{
          count
        }
        preferenceCenter{
          edges{
            node{
              id
              name
            }
          }
        }
      }
    }
    }
    }
  `
    : ''
}

export const mapConsentManagementSettingsDatasourceDetail = (raw: any): DatasourceDetail => {
  const mappedData = raw?.datasources?.edges[0]?.node || {}
  const totalConsentHistoryCount =
    mappedData.consentStats.edges.reduce((acc, curr) => acc + curr.node.consentStats.total, 0) || 0

  return {
    name: mappedData?.name,
    type: mappedData?.type,
    status: mappedData?.status,
    consentHistoryCount: totalConsentHistoryCount,
    communicationPreferencesCount: mappedData?.communicationPreferences?.count,
    preferenceCenterNamesIds: mappedData?.preferenceCenter?.edges?.map((center) => center?.node)
  }
}

//mutation update datasource from consent management
export const mutationConsentManagementUpdateDatasourcesList = (datasourceId: string) => {
  return gql`mutation {
    updateDatasourcesConsentManagement(clientMutationId: "update_datasource",datasourceId: "${datasourceId}") {
      clientMutationId
      datasourceId
    }
  }
   `
}

//mutation removing datasource from consent management
export const mutationConsentManagementDeleteDatasource = (datasourceId: string) => {
  return gql`mutation{
    removeConsentManagementDatasource(
      clientMutationId:"delete_datasource",
      datasourceId:"${datasourceId}"
    ){
      datasourceId
      clientMutationId
    }
  }
   `
}

export const queryConsentManagementSupportedDataSourceTypes = (): string => {
  return gql`
    {
      consentManagementSettings {
        edges {
          node {
            datasourceTypes
          }
        }
      }
    }
  `
}

export const mapConsentManagementSupportedDataSourceTypes = (raw: any): string[] => {
  const mappedData = raw?.consentManagementSettings?.edges?.[0]?.node?.datasourceTypes
  return mappedData || []
}

export const queryConsentManagementSettingsPreferenceCenterListByDatasourceId = (
  datasourceId: string
): string => {
  return gql`
    {
      preferenceCenter(filter: [{key: DATASOURCE_ID, values: ["${datasourceId}"]}]) {
        edges {
          node {
            id
            preferenceCenterGroupId
				    name
            datasource{
          edges{
            node{
              id
              name
              type
            }
          }
        }
          }
        }
      }
    }
    `
}

export const mapConsentManagementSettingsPreferenceCenterListByDatasourceId = (raw: any) => {
  return raw?.preferenceCenter?.edges?.map((edge) => ({
    id: edge?.node?.id,
    preferenceCenterGroupId: edge?.node?.preferenceCenterGroupId,
    name: edge?.node?.name,
    datasourceList: edge?.node?.datasource?.edges?.map((ds) => ({
      id: ds?.node?.id,
      name: ds?.node?.name,
      type: ds?.node?.type
    }))
  }))
}

export const mutationConsentManagementScanDatasource = (datasourceId: string) => {
  return gql`mutation{
      consentManagementDatasourceScanSync(
        datasourceId:"${datasourceId}",
        clientMutationId:"scan_datasource"
      ){
        clientMutationId
        datasourceId
      }
    }
   `
}

export const queryStructDSDatabasesAndClusters = (datasourceId: string) => {
  return `
    {
      datasources(id: "${datasourceId}") {
        edges{
          node{
            id
            name
            type
            databases{
              edges{
                node{
                  name
                  id
                  tableClusters {
                    edges {
                      node{
                        __typename
                        ... on AnchorTableCluster{
                          id
                          name
                        }
                      }
                    }
                  }

                }
              }
            }
          }
        }
      }
    }
   `
}

export const mapConsentManagementStructDSDatabasesAndClusters = (
  raw: any
): DatabasesAndClustersOption[] => {
  const databaseOptions: DatabasesAndClustersOption[] = []

  raw?.datasources?.edges?.forEach(({ node }) => {
    const databases = node?.databases?.edges?.map(({ node }) => {
      const clusters = node?.tableClusters?.edges?.map(({ node }) => {
        if (!node?.id || node?.__typename !== CLUSTER_TYPE_NAME) return
        return {
          id: node?.id,
          name: node?.name
        }
      })

      return {
        id: node?.id,
        name: node?.name,
        clusterOptions: clusters?.filter(Boolean)
      }
    })

    databaseOptions.push(...databases)
  })

  return databaseOptions || []
}

export const queryStructTablesByDatabaseId = (params: StructTablesParams) => {
  const { datasourceId, databaseId, tableSearch } = params
  const searchQuery = tableSearch ? `query: "${tableSearch}",` : ''

  return `{
        email_attribute: attribute(filter: { key: NAME, values: "Email Address" }) {
        edges {
          node {
            id
          }
        }
      }
      datasources(id: "${datasourceId}") {
        edges {
          node {
            tables(${searchQuery} filter: [{key: DATABASE, values: "${databaseId}"}]) {
              count
              edges {
                node {
                  id
                  name
                  columns {
                    edges {
                      node {
                        id
                        name
                        type
                        classifications {
                          pii {
                            attributeId
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryStructTablesByDatabaseId = (
  raw: any
): { structTables: StructTable[]; emailAttributeId: string } => {
  const structTables = raw.datasources.edges[0].node.tables.edges
    .map(({ node }) => ({
      id: node.id,
      name: node.name,
      columns: node.columns?.edges.map(({ node }) => ({
        ...node,
        piiAttributeId: node.classifications?.pii?.attributeId
      }))
    }))
    .filter((table) => table.columns.length > 0)

  const emailAttributeId = raw.email_attribute.edges[0]?.node?.id || ''

  return {
    structTables,
    emailAttributeId
  }
}

export const queryConsentMngTableSamples = (
  params: ConsentManagementTableSamplesParams
): string => {
  return gql`
    {
      tables(id: "${params.tableId}") {
        edges {
          node {
            id
            name
            sampleData(rowCount: ${CM_SAMPLE_DATA_ROWS_COUNT}) {
              header
              rows
            }
          }
        }
      }
    }
  `
}
export const mapConsentMngTableSamples = (raw: any): ConsentManagementTableSample[] => {
  try {
    const sampleData = raw.tables?.edges[0]?.node?.sampleData
    const result: ConsentManagementTableSample[] =
      sampleData?.rows
        .filter((row) => row.length > 0)
        .map((row) =>
          row.reduce(
            (acc, currValue, currIndex) => ({
              ...acc,
              [sampleData?.header[currIndex]]: currValue
            }),
            {}
          )
        ) || []
    return result
  } catch (error) {
    console.error(error)
    throw error
  }
}
